/**
 * tenancy.ts — THE CUSTOMER CONTROL PLANE'S REQUESTS, AND AN HONEST LEDGER OF
 * THE READS THAT DO NOT EXIST.
 *
 * ===========================================================================
 * ONE CANONICAL CUSTOMER STATE
 * ===========================================================================
 * The console is an operational VIEW over product state read from the same
 * server. It does not keep a customer record, an onboarding tracker, a
 * checklist, or any store of its own. There is exactly one place a number on
 * these screens may come from: a response body the platform API sent.
 *
 * That rule has a consequence this file exists to state rather than hide. The
 * platform surface today serves ONE customer act and ONE platform-wide read:
 *
 *   POST /api/system/customers   create a customer organization  (lane PROVISION)
 *   GET  /api/system/jobs/health platform queue health           (on trunk)
 *
 * and NOTHING that reads one customer's estate. Every per-customer read the
 * control plane wants — the directory, the canonical onboarding checklist,
 * people, sites, programs, integrations, operating jurisdictions, activity —
 * exists only as a TENANT-scoped route, guarded by a tenant permission against
 * the caller's OWN organization. A platform operator has no session in the
 * customer's tenant and must never be given one.
 *
 * ===========================================================================
 * WHY THE GAP IS DECLARED INSTEAD OF FILLED
 * ===========================================================================
 * There were three ways to build a customers list on top of that, and two of
 * them are the defect:
 *
 *   1. Keep what the console learns and show it back. That is a second
 *      canonical state. It would say a customer is at step 4 of 14 forever,
 *      because nothing would ever tell it otherwise, and it would be WRONG in
 *      the direction that matters: confident.
 *   2. Render the missing reads as empty tables and zero counts. "0 sites" is
 *      then indistinguishable from "we never looked", which the lane contract
 *      names as the single most common defect in this codebase.
 *   3. Declare each read, mark whether the server serves it, never call the
 *      ones it does not, and render those as the absence they are.
 *
 * This is (3). `PLATFORM_READS` is the ledger, `console-customer-reads.test.ts`
 * checks every row of it against the API's own source in both directions, and
 * the screens render a `pending` row as "no platform read serves this" with the
 * fields it would have supplied named out loud.
 */
import type { ConsoleRequest } from './requests.js';

// ---------------------------------------------------------------------------
// The requests the console can actually send
// ---------------------------------------------------------------------------

/**
 * The body `POST /api/system/customers` accepts, field for field.
 *
 * Bound to `createCustomerSchema` in
 * `apps/api/src/domains/tenancy/platformProvisioningRoutes.ts` (branch
 * `lane/operator-provisioning`). The nesting is load-bearing: `administrator`
 * is an OBJECT there, not two flat fields, and a console that posted
 * `adminName`/`adminEmail` — which is the shape the older public signup route
 * uses — would be refused 400 by a schema that looks almost identical.
 */
export interface CreateCustomerInput {
  readonly name: string;
  readonly slug: string;
  readonly timezone: string;
  readonly administrator: {
    readonly name: string;
    readonly email: string;
  };
}

export function createCustomer(input: CreateCustomerInput): ConsoleRequest {
  return {
    method: 'POST',
    path: '/api/system/customers',
    // Sent verbatim and NOT normalised here. The server trims, lowercases the
    // slug and lowercases the email itself, and it is the server's
    // normalisation that decides what the customer is called. A console that
    // pre-normalised would be able to disagree with it silently.
    body: {
      name: input.name,
      slug: input.slug,
      timezone: input.timezone,
      administrator: { name: input.administrator.name, email: input.administrator.email },
    },
  };
}

/**
 * The one platform read on this surface that is served today.
 *
 * It is platform-WIDE, not per-customer — it says nothing about any particular
 * organization — and it takes `platform:tenancy.administer` with no operator
 * required. It is here because it is the only thing on this area that can tell
 * an operator their tenancy credential works at all before they use it to
 * create a real customer.
 */
export function jobsHealth(): ConsoleRequest {
  return { method: 'GET', path: '/api/system/jobs/health' };
}

/**
 * The customer directory. SIX IDENTITY COLUMNS AND NOT ONE MORE.
 *
 * `platform_customer_directory()` returns what a customer is called, what it is
 * addressed as, whether it is switched on, what zone its days are computed in
 * and when it came into existence. Deliberately no user count, no last-activity
 * timestamp and no "health" — each would be a cross-tenant read of tenant
 * BUSINESS data dressed as an operator convenience. Those remain pending reads
 * below and the console still renders them as gaps.
 */
export function customers(): ConsoleRequest {
  return { method: 'GET', path: '/api/system/customers' };
}

/** `GET /api/system/customers/summary` — the platform-wide, counts-only customer summary. */
export function customerSummary(): ConsoleRequest {
  return { method: 'GET', path: '/api/system/customers/summary' };
}

/**
 * ONE CUSTOMER'S CANONICAL ONBOARDING STATE.
 *
 * The same `getOnboardingStatus` the customer's own administrator gets from
 * `GET /api/onboarding/status`, plus exactly one added key: `totalSteps`. The
 * route binds the tenant through `cwos_tenant_setter` and drops to `cwos_app`,
 * so the rows come back under forced RLS as the least-privilege role — nothing
 * is bypassed and there is no second copy of the step list on the path.
 *
 * A NON-EXISTENT ORGANIZATION IS 404, NOT AN EMPTY CHECKLIST. The route
 * resolves the identity through the directory first, because under forced RLS a
 * binding to an id no organization has reads zero rows everywhere and would
 * otherwise render as a pristine checklist for a fabricated customer.
 */
export function customerOnboarding(orgId: string): ConsoleRequest {
  return {
    method: 'GET',
    path: `/api/system/customers/${encodeURIComponent(orgId)}/onboarding`,
  };
}

/**
 * ONE CUSTOMER'S ADOPTION, AS INTEGERS. People by role (org_admin, supervisor,
 * worker) and status, and invitations by state. No name, email or person id:
 * the server counts inside the bound tenant and sends numbers only.
 */
export function customerAdoption(orgId: string): ConsoleRequest {
  return {
    method: 'GET',
    path: `/api/system/customers/${encodeURIComponent(orgId)}/adoption`,
  };
}

/**
 * ONE CUSTOMER'S CONNECTORS: the customer Integrations screen's own status
 * projection (allowlisted, no config, no ids) plus failure counts by closed
 * error code and a quarantined-record count.
 */
export function customerIntegrations(orgId: string): ConsoleRequest {
  return {
    method: 'GET',
    path: `/api/system/customers/${encodeURIComponent(orgId)}/integrations`,
  };
}

/**
 * ONE CUSTOMER'S GO-LIVE READINESS: counts from the tenant's own records (sites,
 * programs and links, EVV configuration in effect, rules, location policies,
 * active administrators) and the first administrator's invitation state.
 */
export function customerReadiness(orgId: string): ConsoleRequest {
  return {
    method: 'GET',
    path: `/api/system/customers/${encodeURIComponent(orgId)}/readiness`,
  };
}

/** Every request shape this module can build — the fixture the binding test walks. */
export function allTenancyRequestShapes(): ConsoleRequest[] {
  return [
    createCustomer({
      name: 'Synthetic Organization',
      slug: 'synthetic-organization',
      timezone: 'America/Chicago',
      administrator: { name: 'Synthetic Administrator', email: 'operator@example.invalid' },
    }),
    customers(),
    customerOnboarding('00000000-0000-4000-8000-000000000000'),
    customerAdoption('00000000-0000-4000-8000-000000000000'),
    customerIntegrations('00000000-0000-4000-8000-000000000000'),
    customerReadiness('00000000-0000-4000-8000-000000000000'),
    jobsHealth(),
  ];
}

/**
 * One row of `platform_customer_directory()`. Mirrors `CustomerDirectoryEntry`
 * in `apps/api/src/domains/tenancy/index.ts` field for field.
 *
 * `status` is `organization.status` — one of active / suspended / disabled. A
 * SUSPENDED CUSTOMER IS LISTED. Filtering it out would hide the customers an
 * operator is most likely to be looking for: "nobody can sign in" is exactly
 * when somebody opens this console, and a directory that answered by omission
 * would say the organization does not exist.
 */
export interface CustomerDirectoryEntry {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly status: string;
  readonly timezone: string;
  readonly createdAt: string;
}

/**
 * `count` is the LENGTH OF WHAT WAS RETURNED and not a separate query — the
 * route computes it as `customers.length`. The console renders the server's
 * number and checks it against the rows it actually received, because two
 * numbers that can disagree is how a console renders "3 customers" over a list
 * of two.
 */
export interface CustomerDirectory {
  readonly customers: readonly CustomerDirectoryEntry[];
  readonly count: number;
}

/**
 * `GET /api/system/customers/summary` → 200. Counts only, across every org.
 * Mirrors `PlatformCustomerSummary` in apps/api (platformSummary.ts).
 */
export interface PlatformCustomerSummary {
  readonly totalOrganizations: number;
  readonly byStatus: Readonly<Record<string, number>>;
  readonly activation: {
    readonly activated: number;
    readonly onboarding: number;
    readonly notStarted: number;
  };
  readonly workforce: {
    readonly orgsWithActiveWorkforce: number;
    readonly workersInvitedOrActive: number;
    readonly supervisorsActive: number;
  };
  readonly exceptions: { readonly orgsWithConnectorNeverSynced: number };
  readonly measuredAt: string;
}

type StatusCounts = Readonly<Record<'invited' | 'active' | 'suspended' | 'deactivated', number>>;

/** `GET /api/system/customers/:orgId/adoption` → 200. Mirrors `CustomerAdoption` in apps/api. */
export interface CustomerAdoptionRead {
  readonly organization: CustomerDirectoryEntry;
  readonly adoption: {
    readonly people: Readonly<Record<'org_admin' | 'supervisor' | 'worker', StatusCounts>>;
    readonly invitations: {
      readonly pending: number;
      readonly pendingPastExpiry: number;
      readonly accepted: number;
      readonly revoked: number;
      readonly expired: number;
    };
  };
  readonly measuredAt: string;
}

export type FirstAdminState =
  'none' | 'pending' | 'pending_past_expiry' | 'accepted' | 'revoked' | 'expired';

/** `GET /api/system/customers/:orgId/readiness` → 200. Mirrors `CustomerReadiness` in apps/api. */
export interface CustomerReadinessRead {
  readonly organization: CustomerDirectoryEntry;
  readonly readiness: {
    readonly sites: { readonly active: number; readonly withCoordinates: number };
    readonly programs: {
      readonly active: number;
      readonly linkedToSite: number;
      readonly withWorkers: number;
    };
    readonly evv: {
      readonly serviceMappingsInEffect: number;
      readonly attestationPoliciesInEffect: number;
    };
    readonly rules: {
      readonly enabled: number;
      readonly simulating: number;
      readonly suspended: number;
    };
    readonly locationPolicies: number;
    readonly scheduling: {
      readonly shiftsNext14Days: number;
      readonly servicesNext14Days: number;
      readonly workersWithProfile: number;
    };
    readonly workforce: {
      readonly workersInvitedOrActive: number;
      readonly supervisorsActive: number;
    };
    readonly integrations: { readonly enabled: number; readonly everSynced: number };
    readonly milestones: readonly { readonly key: string; readonly done: boolean }[];
    readonly firstAdmin: {
      readonly activeAdmins: number;
      readonly invitation: FirstAdminState;
      readonly invitationExpiresAt: string | null;
    };
  };
  readonly measuredAt: string;
}

/** One connector, as `GET /api/system/customers/:orgId/integrations` returns it. */
export interface CustomerConnector {
  readonly kind: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly needsAttention: boolean;
  readonly openIssues: { total: number; critical: number; warning: number } | null;
  readonly enabled: boolean;
  readonly hasCredentials: boolean;
  readonly lastSyncedAt: string | null;
  readonly staleness: string;
  readonly historicalSyncComplete: boolean;
  readonly historicalSyncPhase: string;
  readonly syncRunning: boolean;
  readonly syncErrorCategory: string | null;
  readonly failuresInWindow: { readonly total: number; readonly byCode: Record<string, number> };
  readonly quarantinedRecordsInWindow: number;
}

/** `GET /api/system/customers/:orgId/integrations` → 200. */
export interface CustomerIntegrationsRead {
  readonly organization: CustomerDirectoryEntry;
  readonly connectors: readonly CustomerConnector[];
  readonly windowDays: number;
  readonly measuredAt: string;
}

/** `GET /api/system/customers/:orgId/onboarding` → 200. */
export interface CustomerOnboardingRead {
  readonly organization: CustomerDirectoryEntry;
  readonly onboarding: ServerOnboarding;
}

// ---------------------------------------------------------------------------
// The response the provisioning door sends back
// ---------------------------------------------------------------------------

/**
 * `POST /api/system/customers` → 201. Mirrors the reply in
 * `platformProvisioningRoutes.ts` field for field.
 *
 * NOTE WHAT IS NOT HERE: no invitation token and no invitation URL outside
 * development. That withholding is Gate A / H-8 and it is the server's, not
 * the console's — but the console must never render a blank where the token
 * would be, because "no link" and "the link is withheld from this reply" are
 * different sentences and only one of them means "go and read your email".
 */
export interface CreatedCustomer {
  readonly orgId: string;
  readonly name: string;
  readonly slug: string;
  readonly timezone: string;
  readonly administrator: {
    readonly userId: string;
    readonly name: string;
    readonly email: string;
    /** `app_user.status`. `invited` until the person accepts — never `active`. */
    readonly state: string;
    readonly invitationId: string;
    readonly invitationExpiresAt: string;
    /** What happened to the delivery JOB. Remedy: run a worker. */
    readonly delivery?: string;
    /** What exists to SEND it. Remedy: set a mail credential. */
    readonly provider?: string;
  };
  readonly onboarding: ServerOnboarding;
  readonly continueSetup?: {
    readonly appUrl?: string;
    readonly onboardingPath?: string;
    /** DEVELOPMENT ONLY. Absent in staging and production, by design. */
    readonly invitationUrl?: string;
  };
}

/**
 * The onboarding block the server reports. It is `OnboardingStatus` MINUS
 * `checklist`, and the missing member is the whole reason the onboarding
 * section below cannot render fourteen rows.
 *
 * `checklist` is declared optional so that the day a platform read supplies it
 * the console renders the canonical rows without a code change — and so that
 * `onboardingProgress` has something real to test its two branches against.
 */
export interface ServerOnboarding {
  readonly orgId?: string;
  readonly currentStep: string;
  /** EXHAUSTIVE. The server filters the canonical step list, so a step absent
   *  from here is a MEASURED incomplete, not an unknown. */
  readonly completedSteps: readonly string[];
  readonly percentComplete: number;
  readonly isLive: boolean;
  /**
   * THE DENOMINATOR, AND IT TRAVELS WITH THE NUMERATOR.
   *
   * `totalSteps` is `checklist.length` on the server — the length of the list
   * it just built, never a constant. That is what lets this console render
   * "3 of 14" WITHOUT KNOWING WHAT 14 IS. The console's refusal to hold its own
   * copy of the step list has not been relaxed; the number simply exists now,
   * and it is still the server's.
   *
   * Optional because the provisioning 201 does not carry it: that reply has no
   * `checklist` either, so there is nothing on that path to take a length of.
   */
  readonly totalSteps?: number;
  /**
   * EVERY STEP IS PRESENT, and an incomplete one carries `completedAt: null`
   * rather than being omitted. That is what makes the absence of a row a real
   * surprise rather than the ordinary case.
   */
  readonly checklist?: readonly {
    readonly step: string;
    readonly completedAt: string | null;
    readonly completedBy: string | null;
  }[];
}

// ---------------------------------------------------------------------------
// The read ledger
// ---------------------------------------------------------------------------

/**
 * `served`  the API registers this route today; the console may call it.
 * `pending` no route serves it. THE CONSOLE NEVER CALLS A PENDING READ — it
 *           renders the absence. Knocking on a route known not to exist would
 *           produce a 404 the operator has to interpret, and an unknown-route
 *           line in the platform log, to learn something this file already
 *           knows.
 */
export type ReadStatus = 'served' | 'pending';

export interface PlatformRead {
  readonly id: string;
  readonly label: string;
  readonly method: 'GET' | 'POST';
  /** The route pattern exactly as the server would register it. */
  readonly route: string;
  readonly status: ReadStatus;
  /**
   * Only for `served`: the branch that carries the route when it is not yet on
   * the integration branch. `null` when the route is on trunk.
   */
  readonly carriedBy: string | null;
  /** The console fields this read — and only this read — could supply. */
  readonly supplies: readonly string[];
  /** Where the data lives today, and why a platform operator cannot reach it. */
  readonly heldBy: string;
}

export const PLATFORM_READS: readonly PlatformRead[] = [
  {
    id: 'customers.create',
    label: 'Provision a customer organization',
    method: 'POST',
    route: '/api/system/customers',
    status: 'served',
    carriedBy: 'lane/operator-provisioning',
    supplies: [
      'organization UUID',
      'organization name, slug, timezone',
      'first-administrator identity and invited state',
      'invitation expiry, delivery job outcome, mail provider state',
      'the onboarding snapshot AS AT PROVISIONING',
    ],
    heldBy: 'the provisioning door itself',
  },
  {
    id: 'platform.jobsHealth',
    label: 'Platform queue health',
    method: 'GET',
    route: '/api/system/jobs/health',
    status: 'served',
    carriedBy: null,
    supplies: ['whether the platform job queue is healthy, degraded or blocked'],
    heldBy: 'the platform system routes',
  },
  {
    id: 'customers.directory',
    label: 'The customer directory',
    method: 'GET',
    route: '/api/system/customers',
    status: 'served',
    carriedBy: null,
    /**
     * THE SUPPLIES LIST WAS CORRECTED WHEN THE ROUTE LANDED, AND THE
     * CORRECTION IS THE POINT.
     *
     * While this read was pending, the ledger claimed it would carry onboarding
     * progress, first-administrator state, Live/Not Live and last setup
     * activity per customer. It carries NONE of those: the route returns the
     * six identity columns of the tenant root and nothing derived, deliberately
     * — each of the others would be a cross-tenant read of tenant business data
     * dressed as an operator convenience. Leaving the old list in place would
     * have been a ledger describing a route that does not exist, which is the
     * same lie the `pending` rows exist to prevent, told about a `served` one.
     *
     * Onboarding progress and Live/Not Live are per-CUSTOMER and live on
     * `customer.onboarding`, one call per customer opened. The console does not
     * fan that out across the directory: N+1 platform reads to decorate a list
     * is a load the operator did not ask for and a per-row failure mode with
     * nowhere honest to render it.
     */
    supplies: [
      'every organization, including suspended and disabled ones',
      'organization name, stable UUID and slug',
      'organization status (active / suspended / disabled)',
      'timezone and creation time',
      'the server’s own count of what it returned',
    ],
    heldBy: 'the platform provisioning door, over platform_customer_directory()',
  },
  {
    id: 'customer.overview',
    label: 'One customer, read back',
    method: 'GET',
    route: '/api/system/customers/:orgId',
    status: 'pending',
    carriedBy: null,
    supplies: [
      'the customer identity as it stands NOW rather than as at provisioning',
      'organization status (in service / suspended)',
      'provisioning state',
    ],
    heldBy:
      'GET /organization — tenant-scoped, answered for the CALLER’s own organization only. ' +
      'POST /api/system/orgs/:orgId/status can WRITE the status a platform operator cannot READ.',
  },
  {
    id: 'customer.onboarding',
    label: 'The canonical onboarding checklist',
    method: 'GET',
    route: '/api/system/customers/:orgId/onboarding',
    status: 'served',
    carriedBy: null,
    supplies: [
      'the full canonical step list, every step present, incomplete ones carrying completedAt: null',
      'totalSteps — the denominator, taken from the length of the list the server just sent',
      'completion timestamp and completing user per step',
      'current step, completed steps, percent complete and isLive',
      'a CURRENT reading rather than a provisioning-time snapshot',
    ],
    heldBy:
      'the platform provisioning door, calling the identical getOnboardingStatus that ' +
      'GET /api/onboarding/status calls for the customer’s own administrator.',
  },
  {
    id: 'customer.adoption',
    label: 'Adoption counts',
    method: 'GET',
    route: '/api/system/customers/:orgId/adoption',
    status: 'served',
    carriedBy: null,
    supplies: [
      'administrators, supervisors and workers, each counted by account status',
      'invitations by state, including pending ones already past expiry',
      'integers only: no name, email or person id leaves the tenant',
    ],
    heldBy: 'the platform provisioning door, binding the one tenant and counting under forced RLS.',
  },
  {
    id: 'customer.people',
    label: 'People',
    method: 'GET',
    route: '/api/system/customers/:orgId/people',
    status: 'pending',
    carriedBy: null,
    supplies: ['worker counts', 'invited / accepted / inactive split', 'administrator roster'],
    heldBy: 'GET /people and GET /directory — tenant-scoped.',
  },
  {
    id: 'customer.readiness',
    label: 'Go-live readiness',
    method: 'GET',
    route: '/api/system/customers/:orgId/readiness',
    status: 'served',
    carriedBy: null,
    supplies: [
      'active sites, and how many have coordinates (a count, never a coordinate)',
      'active programs, how many are linked to an active site and how many have workers',
      'EVV service mappings and attestation policies in effect today',
      'workflow rules enabled, simulating and suspended; location policies',
      'active administrators, and the first administrator invitation state and expiry',
      'shifts and client services scheduled in the next 14 days; scheduling profiles',
      'workers invited or active, active supervisors; integrations enabled and ever synced',
      'ten ordered implementation milestones, each done or not',
    ],
    heldBy:
      'the platform provisioning door, binding the one tenant and counting under forced RLS. ' +
      'Rosters (names, addresses) stay in the customer app.',
  },
  {
    id: 'customer.integrations',
    label: 'Integrations',
    method: 'GET',
    route: '/api/system/customers/:orgId/integrations',
    status: 'served',
    carriedBy: null,
    supplies: [
      "connector kind and the customer app's own status and label",
      'whether a credential is configured (the yes/no ONLY — never a secret value)',
      'last successful sync, staleness, historical-import phase and whether an import is running',
      'failed sync runs in the last 7 days, counted by closed error code',
      'records quarantined in the last 7 days (a count, never a record)',
      'open integration alerts by severity',
    ],
    heldBy:
      'the platform provisioning door, calling the identical listInstanceViews the customer ' +
      'Integrations screen calls, allowlisted: no connector configuration and no ids.',
  },
  {
    id: 'customer.jurisdictions',
    label: 'Operating jurisdictions',
    method: 'GET',
    route: '/api/system/customers/:orgId/jurisdictions',
    status: 'pending',
    carriedBy: null,
    supplies: ['the operating jurisdictions in force, with their effective windows'],
    heldBy:
      'GET /jurisdictions over org_operating_jurisdiction — tenant-scoped. This is the ' +
      'operating-jurisdiction domain and NOT a generic organization setting; reading a ' +
      'jurisdiction off organization settings would be a different fact wearing the same name.',
  },
  {
    id: 'customer.activity',
    label: 'Activity',
    method: 'GET',
    route: '/api/system/customers/:orgId/activity',
    status: 'pending',
    carriedBy: null,
    supplies: ['setup activity, from the existing audit event stream'],
    heldBy:
      'GET /audit/events — tenant-scoped. The console must reuse this stream; it does not get ' +
      'an event system of its own.',
  },
];

export function readById(id: string): PlatformRead {
  const found = PLATFORM_READS.find((r) => r.id === id);
  if (!found) throw new Error(`unknown platform read: ${id}`);
  return found;
}

export function pendingReads(): readonly PlatformRead[] {
  return PLATFORM_READS.filter((r) => r.status === 'pending');
}
